import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState } from "react";
import { showNotification } from "context/notification.service";
import { formTemplatesApi } from "@services/api";
import { FormCategory, FormField } from "types";

const fieldTypeOptions = [
  { value: "text", label: "Texte" },
  { value: "number", label: "Nombre" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Case à cocher" },
  { value: "select", label: "Liste déroulante" },
  { value: "multiselect", label: "Multi-sélection" },
];

export const FormTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const formTemplates = useProjectStore((state) => state.formTemplates);
  const updateFormTemplate = useProjectStore(
    (state) => state.updateFormTemplate,
  );
  const userData = useDataStore((state) => state.userData);

  const existingTemplate = formTemplates.find((t) => t.id === templateId);
  const isNew = !existingTemplate;

  const [name, setName] = useState(existingTemplate?.name || "");
  const [categories, setCategories] = useState<FormCategory[]>(
    existingTemplate ? structuredClone(existingTemplate.categories) : [],
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  const toggleCategory = (catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const updateCategoryName = (catId: string, newName: string) => {
    setCategories((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, name: newName } : c)),
    );
  };

  const addCategory = () => {
    const id = `cat_${Date.now()}`;
    setCategories((prev) => [
      ...prev,
      { id, name: "Nouvelle catégorie", fields: [] },
    ]);
    setExpandedCategories((prev) => new Set(prev).add(id));
  };

  const removeCategory = (catId: string) => {
    setCategories((prev) => prev.filter((c) => c.id !== catId));
  };

  const addField = (catId: string) => {
    const id = `field_${Date.now()}`;
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              fields: [
                ...c.fields,
                {
                  id,
                  label: "Nouveau champ",
                  type: "text" as const,
                  value: "",
                },
              ],
            }
          : c,
      ),
    );
  };

  const removeField = (catId: string, fieldId: string) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? { ...c, fields: c.fields.filter((f) => f.id !== fieldId) }
          : c,
      ),
    );
  };

  const updateFieldLabel = (catId: string, fieldId: string, label: string) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              fields: c.fields.map((f) =>
                f.id === fieldId ? { ...f, label } : f,
              ),
            }
          : c,
      ),
    );
  };

  const updateFieldType = (
    catId: string,
    fieldId: string,
    type: FormField["type"],
  ) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              fields: c.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      type,
                      value:
                        type === "checkbox"
                          ? false
                          : type === "multiselect"
                            ? []
                            : type === "number"
                              ? 0
                              : "",
                      options:
                        type === "select" || type === "multiselect"
                          ? f.options || []
                          : undefined,
                    }
                  : f,
              ),
            }
          : c,
      ),
    );
  };

  const addFieldOption = (catId: string, fieldId: string, option: string) => {
    if (!option.trim()) return;
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              fields: c.fields.map((f) =>
                f.id === fieldId
                  ? { ...f, options: [...(f.options || []), option.trim()] }
                  : f,
              ),
            }
          : c,
      ),
    );
  };

  const removeFieldOption = (
    catId: string,
    fieldId: string,
    optionIndex: number,
  ) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              fields: c.fields.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      options: f.options?.filter((_, i) => i !== optionIndex),
                    }
                  : f,
              ),
            }
          : c,
      ),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du modèle est obligatoire",
      });
      return;
    }

    if (isNew) {
      try {
        const result = await formTemplatesApi.create({
          name,
          org_id: userData?.org_id || "",
          categories,
        });
        useProjectStore.getState().addFormTemplate(result);
        showNotification({
          type: "success",
          message: "Modèle créé avec succès",
        });
        onClose();
      } catch (error) {
        console.error("Error creating form template:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de la création du modèle",
        });
      }
    } else {
      const updated = {
        ...existingTemplate!,
        name,
        categories,
      };
      updateFormTemplate(updated);
      showNotification({
        type: "success",
        message: "Modèle enregistré",
      });
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!existingTemplate) return;
    useProjectStore.getState().removeFormTemplates([existingTemplate.id]);
    showNotification({
      type: "success",
      message: "Modèle supprimé",
    });
    onClose();
  };

  return (
    <BaseModal
      title={isNew ? "Nouveau modèle de formulaire" : "Modifier le modèle"}
      startIcon={<Icon1 name="form" size={20} />}
      onClose={onClose}
      width="max-w-4xl"
    >
      <div className="bg-white flex flex-col gap-4 p-4">
        <TextField
          label="Nom du modèle"
          placeholder="Compte-rendu de chantier"
          value={name}
          onChange={(value) => setName(value)}
        />

        <div className="flex flex-col gap-1">
          <div className="text-h4 text-neutral-400">Catégories / Champs</div>

          <div className="flex flex-col border rounded-xl overflow-hidden">
            {categories.map((cat) => (
              <div key={cat.id} className="border-b last:border-b-0">
                {/* Category header */}
                <div
                  className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                  onClick={() => toggleCategory(cat.id)}
                >
                  <Icon1
                    name={expandedCategories.has(cat.id) ? "down" : "right"}
                    size={12}
                    color="grey"
                  />
                  <div className="flex-1 text-h2 font-medium">{cat.name}</div>
                  <span className="text-h3 text-neutral-400 mr-2">
                    {cat.fields.length} champ(s)
                  </span>
                  <button
                    className="text-red-400 hover:text-red-600 p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCategory(cat.id);
                    }}
                  >
                    <Icon1 name="delete" size={20} color="red" />
                  </button>
                </div>

                {/* Fields */}
                {expandedCategories.has(cat.id) && (
                  <div className="pl-6 pb-2">
                    <div className="px-3 py-2">
                      <TextField
                        label="Nom de la catégorie"
                        placeholder="Nom de la catégorie"
                        value={cat.name}
                        onChange={(value) => updateCategoryName(cat.id, value)}
                      />
                    </div>
                    {cat.fields.map((field) => (
                      <div
                        key={field.id}
                        className="flex flex-col gap-1 px-3 py-0.5"
                      >
                        <div className="flex flex-row items-center gap-2">
                          <div className="flex-1">
                            <TextField
                              placeholder="Nom du champ"
                              value={field.label}
                              onChange={(value) =>
                                updateFieldLabel(cat.id, field.id, value)
                              }
                            />
                          </div>
                          <div className="w-200">
                            <DropDown
                              variant="small"
                              placeholder="Type..."
                              options={fieldTypeOptions}
                              value={field.type}
                              onSelect={(val) =>
                                updateFieldType(
                                  cat.id,
                                  field.id,
                                  val as FormField["type"],
                                )
                              }
                              category="Type"
                            />
                          </div>
                          <button
                            className="text-red-400 hover:text-red-600 p-1 flex"
                            onClick={() => removeField(cat.id, field.id)}
                          >
                            <Icon1 name="delete" size={20} color="red" />
                          </button>
                        </div>

                        {/* Options editor (select & multiselect) */}
                        {(field.type === "select" ||
                          field.type === "multiselect") && (
                          <SelectOptionsEditor
                            options={field.options || []}
                            onAdd={(opt) =>
                              addFieldOption(cat.id, field.id, opt)
                            }
                            onRemove={(idx) =>
                              removeFieldOption(cat.id, field.id, idx)
                            }
                          />
                        )}
                      </div>
                    ))}
                    <button
                      className="flex flex-row items-center gap-1 px-3 py-1 text-h3 text-neutral-400 hover:text-neutral-600"
                      onClick={() => addField(cat.id)}
                    >
                      <Icon1 name="plus" size={12} color="grey" />
                      Champ
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={addCategory}
            >
              Catégorie
            </SecondaryButton>
          </div>
        </div>
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-between">
        <div>
          {!isNew && (
            <SecondaryButton
              variant="small"
              danger={true}
              onClick={handleDelete}
            >
              Supprimer
            </SecondaryButton>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} />
        </div>
      </div>
    </BaseModal>
  );
};

function SelectOptionsEditor({
  options,
  onAdd,
  onRemove,
}: {
  options: string[];
  onAdd: (option: string) => void;
  onRemove: (index: number) => void;
}) {
  const [newOption, setNewOption] = useState("");

  return (
    <div className="flex flex-col gap-2 ml-6 mt-2 mb-1">
      <div className="text-h4 text-neutral-400">Options</div>
      <div className="flex flex-row flex-wrap gap-1.5">
        {options.map((opt, i) => (
          <div
            key={i}
            className="flex flex-row items-center gap-1.5 bg-neutral-100 rounded-full px-4 py-1 text-h3 text-neutral-400"
          >
            <span>{opt}</span>
            <button
              className="text-neutral-400 hover:text-red-500"
              onClick={() => onRemove(i)}
            >
              <Icon1 name="effacer2" size={20} color="grey" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-row gap-2 items-center">
        <TextField
          placeholder="Nouvelle option..."
          value={newOption}
          onChange={(value) => setNewOption(value)}
        />
        <SecondaryButton
          variant="large"
          onClick={() => {
            if (newOption.trim()) {
              onAdd(newOption);
              setNewOption("");
            }
          }}
        >
          Ajouter
        </SecondaryButton>
      </div>
    </div>
  );
}
